"use client"

import { Card, CardContent, smartAnalysisOutcomeExplanation, cn } from '@yeshuman/sdk'
import type { SmartAnalysisTierCounts } from '@yeshuman/sdk'
import { ChartNoAxesColumn } from 'lucide-react'

import { SmartAnalysisSegmentedOutcomeBar } from './smart-analysis-segmented-outcome-bar'

export type SmartAnalysisOutcomeSummaryProps = {
  summary?: string | null
  counts: SmartAnalysisTierCounts
  totalCount: number
  llmJudgedCount?: number | null
  className?: string
}

/** Analysis outcome card: prose summary, segmented bar, and tier explanation. */
export function SmartAnalysisOutcomeSummary({
  summary,
  counts,
  totalCount,
  llmJudgedCount,
  className,
}: SmartAnalysisOutcomeSummaryProps) {
  const summaryText = summary?.trim()
  const explanation = smartAnalysisOutcomeExplanation(counts)
  const metaParts: string[] = []
  if (totalCount > 0) {
    metaParts.push(`${totalCount} ${totalCount === 1 ? 'candidate' : 'candidates'} ranked`)
  }
  if (llmJudgedCount != null && llmJudgedCount > 0) {
    metaParts.push(`${llmJudgedCount} LLM evaluated`)
  }

  return (
    <Card className={cn('gap-0 overflow-hidden p-0', className)}>
      <div className="flex w-full items-center gap-2 px-[var(--sp-card)] py-3">
        <ChartNoAxesColumn className="size-4 shrink-0" aria-hidden />
        <span className="text-sm font-semibold">Analysis summary</span>
      </div>
      <CardContent className="space-y-3 px-[var(--sp-card)] pb-[var(--sp-card)] pt-0">
        {summaryText ? (
          <p className="text-sm text-foreground">{summaryText}</p>
        ) : null}
        <SmartAnalysisSegmentedOutcomeBar counts={counts} />
        <p className="text-sm text-muted-foreground">{explanation}</p>
        {metaParts.length > 0 ? (
          <p className="text-xs text-muted-foreground">{metaParts.join(' · ')}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}
